import { X, ZoomIn, ZoomOut, Expand } from "lucide-react";
import {
  Carousel,
  CarouselApi,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
} from "../shadcn/carousel";
import Image from "next/image";
import { useKeyboardKeydown } from "@/utils/useKeyboardKeydown";
import { useEffect, useRef, useState } from "react";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";

type ImagesCarrouselDialogProps = {
  images: string[];
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  startIndex?: number;
};

const ImagesCarrouselDialog = ({
  images,
  isOpen,
  onOpenChange,
  startIndex = 0,
}: ImagesCarrouselDialogProps) => {
  const [api, setApi] = useState<CarouselApi>();
  const carouselRef = useRef<HTMLDivElement>(null);

  useKeyboardKeydown({
    letter: "Escape",
    action: () => onOpenChange(false),
    ignore_meta_ctrl_key: true,
  });

  useEffect(() => {
    if (api && isOpen && startIndex > 0) {
      api.scrollTo(startIndex, true);
    }
  }, [api, isOpen, startIndex]);

  useEffect(() => {
    if (isOpen && carouselRef.current) {
      carouselRef.current.focus();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed top-0 left-0 w-full h-full bg-black/50 flex items-center justify-center z-5000"
      onClick={() => onOpenChange(false)}
    >
      <div className="absolute top-4 right-4 flex gap-2 z-6000 pointer-events-auto">
        <div
          className="cursor-pointer p-2 bg-black/50 hover:bg-black/80 rounded-md shadow-sm transition-colors"
          onClick={(e) => {
            e.stopPropagation();
            onOpenChange(false);
          }}
          title="Close"
        >
          <X className="text-white w-5 h-5" />
        </div>
      </div>
      <Carousel
        ref={carouselRef}
        setApi={setApi}
        className="bg-transparent max-w-[95vw] max-h-[95vh] w-full select-none focus:outline-none"
        onClick={(e) => e.stopPropagation()}
        tabIndex={0}
      >
        <CarouselContent className="bg-transparent">
          {images.map((image) => (
            <CarouselItem
              key={image}
              className="relative flex items-center justify-center min-h-[85vh] pointer-events-none"
            >
              <TransformWrapper centerOnInit={true}>
                {({ zoomIn, zoomOut, resetTransform }) => (
                  <>
                    <div className="fixed top-4 right-16 flex gap-2 z-6000 pointer-events-auto">
                      <div
                        className="cursor-pointer p-2 bg-black/50 hover:bg-black/80 rounded-md transition-colors shadow-sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          zoomIn();
                        }}
                        title="Zoom In"
                      >
                        <ZoomIn className="text-white w-5 h-5" />
                      </div>
                      <div
                        className="cursor-pointer p-2 bg-black/50 hover:bg-black/80 rounded-md transition-colors shadow-sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          zoomOut();
                        }}
                        title="Zoom Out"
                      >
                        <ZoomOut className="text-white w-5 h-5" />
                      </div>
                      <div
                        className="cursor-pointer p-2 bg-black/50 hover:bg-black/80 rounded-md transition-colors shadow-sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          resetTransform();
                        }}
                        title="Reset Zoom"
                      >
                        <Expand className="text-white w-5 h-5" />
                      </div>
                    </div>
                    <TransformComponent
                      wrapperStyle={{
                        width: "90vw",
                        height: "85vh",
                        pointerEvents: "auto",
                      }}
                      contentStyle={{ width: "100%", height: "100%" }}
                    >
                      <div
                        className="relative w-full h-full flex items-center justify-center"
                        autoFocus
                      >
                        <Image
                          src={image}
                          alt="Image"
                          fill
                          style={{
                            objectFit: "contain",
                            maxWidth: "100%",
                            maxHeight: "100%",
                            backgroundColor: "transparent",
                          }}
                          className="rounded-lg"
                        />
                      </div>
                    </TransformComponent>
                  </>
                )}
              </TransformWrapper>
            </CarouselItem>
          ))}
        </CarouselContent>
        <CarouselPrevious className="left-4" />
        <CarouselNext className="right-4" />
      </Carousel>
    </div>
  );
};

export default ImagesCarrouselDialog;
